import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Table, Space, Popconfirm, Button, message, Input, Form, Typography, Row, Col, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { HomeOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined, CheckOutlined, CloseOutlined
} from "@ant-design/icons";
import axios from "../../axios-interceptor.js";
import BreadCrumb from "../BreadCrumb";
import "../../style/organisationConfig.css"
import { getUserSpaceEntitlements } from "../../utils/currentUserDetail";
import { showLoader } from "../../utils/showloader.js";
import { trimStringValues } from "../../utils/trimStrings";
const { Title } = Typography;
const sampleData = [
    { name: 'Finance', description: "finance category" },
    { name: 'Complaince', description: "" },
]

const BACKEND_API = process.env.REACT_APP_BACKEND_API;


const CategoryMaster = ({ title }) => {
    const { t } = useTranslation();
    let defaultBreadCrumbItems = [{title: <HomeOutlined />, path: "/app/dashboard"}, { title: t('Category Master')}]
    const [categoryData, setCategoryData] = useState(sampleData)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [tempData, setTempData] = useState([])
    const [formData, setFormData] = useState(null)
    const [form] = Form.useForm();
    const { userInfo } = useSelector(state => state.user);
    const adminEntitlements = getUserSpaceEntitlements(null, userInfo.userAccess);

    const fetchCategory = useCallback(async () => {
        showLoader(true)
        await axios({
            method: "GET",
            url: `${BACKEND_API}/category/1`,
            headers: {
                userId: 1,
            },
        })
            .then((response) => {
                let data = response.data
                setCategoryData(data)
                setTempData(data)
            })
            .catch((error) => {
            });
        showLoader(false)
    }, [])


    useEffect(() => {
        fetchCategory();
    }, [fetchCategory])

    const onAllowEdit = (category) => {
        let tempList = categoryData.map((data, index) => {
            if (data.categoryId === category.categoryId) {
                return { ...data, isEditable: true }
            } else {
                return { ...data }
            }
        })
        setTempData(tempList)
        setSelectedCategory({ ...category })
        form.setFieldsValue({ name: category.name, description: category.description, categoryId: category.categoryId  })
        setFormData({ ...category })
    }

    const handleEditCategory = (category, rowIndex) => {
        let editableRows = tempData.filter(data => data.isEditable);
        if(editableRows.length > 0 ) {
            Modal.confirm({
                title: t('Confirm'),
                content: t("You haven't saved the current category. Are you sure to continue?"),
                okText:t("Yes"),
                onOk:(event) => {
                    onAllowEdit(category, rowIndex);
                    Modal.destroyAll()
                },
                footer: (_, { OkBtn, CancelBtn }) => (
                  <>
                    <CancelBtn />
                    <OkBtn />
                  </>
                ),
              });
        } else {
            onAllowEdit(category, rowIndex)
        }        
    }

    const handleDeleteCategory = async(record) => {
        showLoader(true)
        await axios({
            method: "DELETE",
            url: `${BACKEND_API}/category/${record.tenantId}?categoryId=${record.categoryId}`,
            headers: {
                userId: 1,
            },
        })
            .then((response) => {
                message.success(t('Category deleted successfully'))
                fetchCategory()
            })
            .catch((error) => {
            });
            showLoader(false)
    }

    const onAddNewCategory = () => {
        setSelectedCategory({})
        const newData = {
            key: tempData.length + 1,
            name: "",
            description: "",
            isEditable: true,
        };
        form.resetFields();
        setFormData({ ...newData })
        let modEnteredData = tempData.map((data, index) => {
            return {
                ...data,
                key: index + 1
            }
        })
        modEnteredData.push(newData)
        setTempData([...modEnteredData]);
    }

    const changeHandler = (key, value, record, rowIndex) => {
        let updatedData = tempData.map((data, index) => {
            let dataObj = { ...data }
            if (index === rowIndex) {
                setFormData({ ...data, [key]: value })
                return { ...dataObj, [key]: value }
            }
            return dataObj
        })
        setTempData(updatedData)
    }

    const onSaveCategory =async (record) => {
        let postData = trimStringValues({name: formData.name, description: formData.description })
        if(formData.categoryId) {
            postData.categoryId = formData.categoryId
        }
        // return
        showLoader(true)
        await axios({
            method: formData.categoryId ? "PUT" : "POST",
            url: `${BACKEND_API}/category`,
            data: { ...postData, tenantId: localStorage.getItem("tenantId") },
            headers: {
                Accept: "application/json",
                userId: 1,
            },
        })
        .then((response) => {
            message.success(
                selectedCategory
                    ? t("Category updated successfully")
                    : t("Category created successfully")
            );
            fetchCategory();
            onSaveAndEditCancel('save');
        })
        .catch((error) => {
            // TODO: move it to success
        });
        showLoader(false)
        setSelectedCategory(null)
        setFormData(null)
    }

    const onSaveAndEditCancel = (type='cancel') => {
        if (type === 'cancel') {
            setTempData([...categoryData])
        }
        setSelectedCategory(null)
        setFormData(null)
    }

    const onCheck = async (record) => {
        try {
            const values = await form.validateFields();
            onSaveCategory({...record, name: values.name, description: values.description})
        } catch (errorInfo) {
        }
    }

    const tableColumns = [
        {
            title: t("Name"),
            dataIndex: "name",
            key: "name",
            width: "20%",
            render: (text, record, rowIndex) => {
                return (
                    record.isEditable ?
                        <Form.Item name="name" rules={[
                            {
                                required: true,
                                whitespace: true,
                                message: t('Name is required')
                            },
                            {
                                validator: async (_, value) => {
                                    if(!value) return;
                                    let valExists = tempData.find(data => data.name === value && data.categoryId && data.categoryId !== record.categoryId);
                                    if(!valExists) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('Category already exists')))
                                }
                            }
                        ]}>
                            <Input value={record.name} type="text" placeholder={t("Enter name")} onChange={(e) => changeHandler("name", e.target.value, record, rowIndex)} />
                        </Form.Item>
                        : text
                )
            }
        },
        {
            title: t("Description"),
            dataIndex: "description",
            key: "description",
            width: "30%",
            render: (text, record, rowIndex) => {
                return (
                    record.isEditable ? <Input value={record.description} type="text" placeholder={t("Enter description")} onChange={(e) => changeHandler("description", e.target.value, record, rowIndex)} />
                        : text
                )
            }
        }
    ];

    if(adminEntitlements.includes("Category_Master_Update")) {
        tableColumns.push(
            {
                title: t("Action"),
                dataIndex: "",
                key: "",
                width: "15%",
                render: (text, record, rowIndex) => {
                    return (
                        !record.isEditable ?
                            <Space>
                                <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditCategory(record, rowIndex)}
                                />
                                <Popconfirm
                                    title={t("Delete the category")}
                                    description={t("Are you sure to delete this category?")}
                                    okText={t("Yes")}
                                    cancelText={t("No")}
                                    onConfirm={() => handleDeleteCategory(record)}
                                >
                                    <Button size="small" icon={<DeleteOutlined />} />
                                </Popconfirm>
                            </Space>
                            : <Space>
                                <Button type="primary" size="small" onClick={() => onCheck(record)} icon={<CheckOutlined />}></Button>
                                <Button size="small" onClick={() => onSaveAndEditCancel('cancel', rowIndex)} icon={<CloseOutlined />}></Button>
                            </Space>
                    )
                },
            }
        )
    }

    return (
        <div className="page-wrapper">
            <BreadCrumb items={defaultBreadCrumbItems} marginb="24px" />

            <Row
                gutter={8}
                style={{
                    margin: "0",
                    paddingBottom: "20px",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                }}
            >
                <Col span={12}>
                    <Title level={3} style={{ margin: 0 }}>{t(title)}</Title>
                </Col>
            </Row>
            <div className="organisation-structure" style={{ width: "100%", paddingRight: "10px", marginTop: "20px" }}>
                <Form form={form} onFinish={onSaveCategory}>
                    <Table style={{ width: "70%" }} dataSource={tempData} columns={tableColumns} pagination={false} />

                </Form>
                {
                    (!selectedCategory && adminEntitlements.includes("Category_Master_Update")) &&
                    <div style={{ paddingLeft: "14px", marginTop: "10px" }}>
                        <span style={{ fontSize: "14px", color: "#1677ff", cursor: "pointer", padding: "8px" }} onClick={onAddNewCategory}><PlusOutlined style={{ marginRight: "5px" }} /> {t("Add")}</span>
                    </div>
                }
            </div>
        </div>
    )

}

export default CategoryMaster